//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import usePlacesAutocomplete, { getDetails, getGeocode, getLatLng } from 'use-places-autocomplete';
//Third party libraries
import * as Yup from 'yup';
import { projectTypeWithAvgSalesPrice, projectTypeWithUnits } from '@/components/Sherpa/Projects/NewProjectForm/BasicProjectInfo/constants';
//Custom components
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/Form/Checkbox';
import Input from '@/components/UI/Form/Input';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { fetchNeighborhoods } from '@/utils/location';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
//Assets and Miscellaneous
import { States } from '../../../../../../../constants';
import {
  constructionType,
  // neighborHoods,
  projectType,
} from '../../../../../../../constants/Project';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
//Services
// import { getAddressDecode } from "../../../../../../../api/misc";
import { notifyUser } from '../../../../../../../store/Toast';
import type {
  // NeighborHoodsInterface,
  ReactSelectOption,
  State,
} from '../../../../../../../types/Constants';
import type { ProjectDetailType } from '../../../../../../../types/Project';
import { containsObjectInArray, sanitizeResponseError } from '../../../../../../../utils';
import { zipCodeRegEx } from '../../../../../../../utils/RegularExpressions';

const validationSchema = Yup.object({
  name: Yup.string().required('Project name is required').max(50, 'Project name cannot be greater than 50 characters.'),
  address: Yup.string()
    .required('Address is required')
    .min(4, 'At least 4 characters address is required')
    .max(50, 'Address cannot be greater than 50 characters.'),
  city: Yup.string()
    .required('City is required')
    .min(2, 'At least 2 characters city name is required')
    .max(50, 'City name cannot be greater than 50 characters.'),
  state: Yup.object({
    name: Yup.string(),
    abbreviation: Yup.string(),
  })
    .typeError('State is required')
    .required('State is required')
    .test('state', 'You must select a valid state', (val) => {
      return containsObjectInArray(States as { name: string; abbreviation: string }[], val);
    }),
  zipCode: Yup.string().required('Zip code is required').matches(zipCodeRegEx, 'Please enter a valid zip code.'),
  projectType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .typeError('Project type is required')
    .required('Project type is required')
    .test('projectType', 'You must select a valid project type', (val) => {
      return containsObjectInArray(projectType, val);
    }),
  units: Yup.number().when('projectType', {
    is: (val: ReactSelectOption) => projectTypeWithUnits.includes(val.value),
    then: (schema) => schema.required('Required').positive().max(999999999999999).typeError('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
  constructionType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .required('Construction type is required')
    .typeError('Construction type is required')
    .test('constructionType', 'You must select a valid construction type', (val) => {
      return containsObjectInArray(constructionType, val);
    }),
  neighborhood: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  squareFeet: Yup.number().min(1, 'Valid Square feet is required').required('Square feet required'),
  departmentLedProject: Yup.boolean().nullable(),
});

function UpdateProjectBaselineForm({ handleModal }: { handleModal: () => void }) {
  const { projectId } = useParams<string>();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const projectTypeValues = useWatch({ name: 'projectType' });
  const cityValue = useWatch({ name: 'city' });
  const stateValue = useWatch({ name: 'state' });
  const { setValue: setFormValue, getValues } = useFormContext();

  const neighborhood = fetchNeighborhoods({
    state: stateValue.abbreviation,
    city: cityValue,
  });

  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    callbackName: 'initMap',
    requestOptions: { componentRestrictions: { country: 'us' } },
  });

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current?.contains(event.target)) {
        clearSuggestions();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [clearSuggestions]);

  useEffect(() => {
    if (getValues('address').length > 0) {
      setValue(getValues('address'));
    }
  }, [getValues, setValue]);

  const patchProjectBaseline = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        handleModal();
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project baseline saved successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateProjectHandler = async (
    formData: Omit<ProjectDetailType, 'state' | 'projectType' | 'constructionType' | 'neighborhood'> & {
      state: State;
      projectType: ReactSelectOption;
      constructionType: ReactSelectOption;
      // neighborhood: NeighborHoodsInterface;
      neighborhood: { label: string; value: string };
    }
  ) => {
    const address = `${formData.address}, ${formData.city}, ${formData.state.name}, ${formData.zipCode}`;

    getGeocode({ address })
      .then((res: any) => {
        const { lat, lng } = getLatLng(res[0]);

        const projectLocation = {
          address1: formData.address,
          city: formData.city,
          state: formData.state.name,
          postalCode: formData.zipCode.toString(),
          country: 'United States of America',
          lat: lat,
          lng: lng,
          placeId: res[0].place_id,
        };

        const transformedProjectData: Partial<IPatchProjectServicePayload> = {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          state: formData.state.name,
          zipCode: formData.zipCode.toString(),
          projectType: formData.projectType.value,
          units: projectTypeWithUnits.includes(formData.projectType.value) ? formData.units : 0,
          avgSalesPrice: projectTypeWithAvgSalesPrice.includes(formData.projectType.value) ? formData.avgSalesPrice : 0,
          constructionType: formData.constructionType.value,
          neighborhood: formData.neighborhood.value.length > 0 ? formData.neighborhood.value : '-',
          squareFeet: formData.squareFeet,
          location: projectLocation,
          departmentLedProject: formData?.departmentLedProject ? formData?.departmentLedProject : false,
        };
        patchProjectBaseline.mutate(transformedProjectData);
      })
      .catch((_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Unable to resolve entered address',
            toastType: 'failed',
          })
        );
      });
  };

  const handleAddressSelection = (address: any) => {
    const { main_text } = address.structured_formatting;

    getDetails({
      placeId: address.place_id,
      fields: ['address_components'],
    }).then((details) => {
      const { address_components } = details as {
        address_components: {
          long_name: string;
          short_name: string;
          types: any[];
        }[];
        geometry: any;
      };

      const city = address_components.find((ac) => ac.types.includes('locality'));

      const state = address_components.find((ac) => ac.types.includes('administrative_area_level_1'));

      const neighborhood = address_components.find((ac) => ac.types.includes('administrative_area_level_3'));
      const zipCode = address_components.find((ac) => ac.types.includes('postal_code'));

      setFormValue('address', main_text);
      setValue(main_text, false);
      setFormValue('city', city?.long_name);
      setFormValue('state', {
        name: state?.long_name,
        abbreviation: state?.short_name,
      });
      setFormValue('zipCode', zipCode?.long_name);

      setFormValue('neighborhood', neighborhood ? neighborhood?.long_name : '-');
    });

    clearSuggestions();
  };

  return (
    <HookForm className='w-full' onSubmit={updateProjectHandler}>
      <h2 className='mt-0'>Update Project Baseline Information</h2>
      <section className='mt-4 space-y-4'>
        <HookInput type='text' label='Project Name' name='name' />
        {/* <HookInput type="text" label="Address" name="address" /> */}
        <div ref={ref} className='relative col-span-6'>
          <Input
            type='text'
            label='Address'
            value={value}
            name='address'
            error=''
            disabled={!ready}
            changed={(e) => setValue(e.target.value)}
          />
          {status === 'OK' && (
            <ul className='absolute border border-t-0 py-2 flex flex-col gap-2 bg-white w-full z-10'>
              {data.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  className='px-3 py-2 hover:bg-neutralColor-lightest cursor-pointer'
                  onClick={() => handleAddressSelection(suggestion)}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <HookInput type='text' label='City' name='city' />
          {/* <HookInput type="text" label="Neighborhood" name="neighborhood" /> */}
          <HookReactSingleSelect
            id='Neighborhood'
            options={neighborhood
              ?.filter((n) => n?.trim())
              .map((n) => ({
                label: n,
                value: n,
              }))}
            placeholder='Select Neighborhood'
            name='neighborhood'
            label='Neighborhood'
            isCreatable
            isClearable
          />
        </div>
        <div className='grid grid-cols-5 gap-4'>
          <div className='col-span-3'>
            <HookReactSingleSelect
              id='state'
              options={States}
              getOptionLabel={(option) => (option as State).name}
              getOptionValue={(option) => (option as State).abbreviation}
              placeholder='Select State'
              name='state'
              label='State'
            />
          </div>
          <HookInput customClassName='col-span-2' type='number' label='Zip / Postal Code' name='zipCode' />
        </div>
        <div className='col-span-5'>
          <HookReactSingleSelect
            id='projectType'
            options={projectType}
            placeholder='Select Project Type'
            name='projectType'
            label='Project Type'
          />
        </div>
        {(projectTypeWithUnits.includes(projectTypeValues.value) || projectTypeWithAvgSalesPrice.includes(projectTypeValues.value)) && (
          <div className='grid grid-cols-5 gap-4'>
            {projectTypeWithUnits.includes(projectTypeValues.value) && (
              <HookMaskedInput
                customClassName={`${projectTypeWithAvgSalesPrice.includes(projectTypeValues.value) ? 'col-span-2' : 'col-span-5'}`}
                label='Units'
                name='units'
                id='units'
                maskedComponent='numeric'
              />
            )}
            {projectTypeWithAvgSalesPrice.includes(projectTypeValues.value) && (
              <HookMaskedInput
                customClassName='col-span-3'
                label='Average Sales Price'
                name='avgSalesPrice'
                id='avg-sales-price'
                maskedComponent='currency'
              />
            )}
          </div>
        )}
        <HookReactSingleSelect
          id='constructionType'
          options={constructionType}
          placeholder='Select Construction Type'
          name='constructionType'
          label='Construction Type'
        />
        {/* <HookReactSingleSelect
          id="neighborhood"
          options={neighborHoods}
          getOptionLabel={(option) => (option as NeighborHoodsInterface).name}
          getOptionValue={(option) => (option as NeighborHoodsInterface).name}
          placeholder="Select Neighborhood"
          name="neighborhood"
          label="Neighborhood"
        /> */}

        <HookInput type='number' label='Square Feet' name='squareFeet' />
        <Checkbox
          name='departmentLedProject'
          label='Department Led Project'
          defaultChecked={getValues('departmentLedProject')}
          id='department-led-project'
          onChange={(e) => {
            const { checked } = e.target;
            setFormValue('departmentLedProject', checked);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />

        <div className='mt-5 flex justify-end gap-4'>
          <Button label='Cancel' btnStyle='btn-neutral' btnType='button' onClick={handleModal} />
          <Button
            label={`${patchProjectBaseline.isLoading ? 'Saving...' : 'Save'}`}
            btnStyle='btn-primary'
            type='submit'
            disabled={patchProjectBaseline.isLoading}
          />
        </div>
      </section>
    </HookForm>
  );
}

interface IUpdateProjectBaselineProps {
  handleModal: () => void;
}
const UpdateProjectBaseline = (props: IUpdateProjectBaselineProps) => {
  const { projectId } = useParams<string>();
  const fetchProject = useProject({ projectId: Number(projectId) });
  const project = fetchProject?.data?.data;

  // transformed project values to defaultValues
  const selectedState = States.find((s) => s.name === project?.state);
  // const selectedNeighborhood = neighborHoods.find(
  //   (n) => n.name === project.neighborhood
  // );

  const transformedDefaultValue = {
    ...project,
    state: {
      name: selectedState?.name,
      abbreviation: selectedState?.abbreviation,
    },
    projectType: {
      label: project?.projectType,
      value: project?.projectType,
    },
    constructionType: {
      label: project?.constructionType,
      value: project?.constructionType,
    },
    neighborhood: {
      label: project?.neighborhood,
      value: project?.neighborhood,
    },
  };

  return (
    <Modal>
      <HookFormProvider resolver={yupResolver(validationSchema) as any} mode='all' defaultValues={transformedDefaultValue}>
        <UpdateProjectBaselineForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};

export default UpdateProjectBaseline;
